import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Layers } from "lucide-react";

const SETS = [
  ["addGroups", "roleApply.groups"],
  ["addDistributionLists", "roleApply.dls"],
  ["addLicenses", "roleApply.licenses"],
  ["addTeams", "roleApply.teams"],
];

export const RoleTemplateApplyDialog = ({ open, onOpenChange, uid, onApplied }) => {
  const { t } = useLang();
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({});
  const [attrsOn, setAttrsOn] = useState({});

  useEffect(() => {
    if (open) { api.get("/role-templates").then((r) => setTemplates(r.data)).catch(() => {}); }
    if (!open) { setTid(""); setPreview(null); setSel({}); setAttrsOn({}); }
  }, [open]);

  const loadPreview = (id) => {
    setTid(id); setPreview(null);
    if (!id) return;
    setLoading(true);
    api.get(`/users/${uid}/role-template-preview/${id}`).then((r) => {
      setPreview(r.data);
      const s = {};
      SETS.forEach(([k]) => { s[k] = new Set((r.data[k] || []).map((x) => x.id)); });
      setSel(s);
      const a = {}; Object.keys(r.data.attrChanges || {}).forEach((k) => { a[k] = true; });
      setAttrsOn(a);
    }).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };

  const toggle = (setKey, id) => setSel((prev) => {
    const next = new Set(prev[setKey]); next.has(id) ? next.delete(id) : next.add(id);
    return { ...prev, [setKey]: next };
  });

  const totalChanges = SETS.reduce((n, [k]) => n + (sel[k]?.size || 0), 0) + Object.values(attrsOn).filter(Boolean).length;

  const apply = async () => {
    setBusy(true);
    try {
      const attrs = {};
      Object.entries(preview.attrChanges || {}).forEach(([field, ch]) => {
        if (!attrsOn[field]) return;
        attrs[field === "manager" ? "manager" : field] = field === "manager" ? ch.id : ch.new;
      });
      const body = {
        addGroups: [...(sel.addGroups || [])],
        addDistributionLists: [...(sel.addDistributionLists || [])],
        addLicenses: [...(sel.addLicenses || [])],
        addTeams: [...(sel.addTeams || [])],
        attrs,
      };
      const { data } = await api.post(`/users/${uid}/apply-role-template`, body);
      toast.success(t("roleApply.applied"), { description: JSON.stringify(data.counts) });
      onOpenChange(false);
      onApplied && onApplied();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="role-apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">{t("roleApply.title")}</DialogTitle>
          <DialogDescription>{t("roleApply.desc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("roleApply.pick")}</label>
          <Select value={tid} onValueChange={loadPreview}>
            <SelectTrigger className="rounded-sm border-zinc-300 text-sm" data-testid="role-apply-select"><SelectValue placeholder={t("roleApply.pick")} /></SelectTrigger>
            <SelectContent>{templates.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading && <div className="flex items-center gap-2 py-6 text-sm text-zinc-500"><Loader2 size={16} className="animate-spin" /> {t("roleApply.computing")}</div>}

        {preview && !loading && (
          <div className="space-y-4">
            {totalChanges === 0 && <p className="border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="role-apply-nochange">{t("roleApply.noChanges")}</p>}
            {SETS.map(([k, lbl]) => (preview[k] || []).length > 0 && (
              <div key={k}>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500">{t(lbl)}</div>
                <div className="space-y-1.5">
                  {preview[k].map((it) => (
                    <label key={it.id} className="flex cursor-pointer items-center gap-2.5 text-sm text-zinc-700" data-testid={`role-apply-item-${it.id}`}>
                      <Checkbox checked={sel[k]?.has(it.id)} onCheckedChange={() => toggle(k, it.id)} /> {it.name}
                      <span className="text-[10px] font-bold uppercase text-emerald-600">+ {t("roleApply.add")}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {Object.keys(preview.attrChanges || {}).length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("roleApply.attrs")}</div>
                <div className="space-y-1.5">
                  {Object.entries(preview.attrChanges).map(([field, ch]) => (
                    <label key={field} className="flex cursor-pointer items-center gap-2.5 text-sm text-zinc-700" data-testid={`role-apply-attr-${field}`}>
                      <Checkbox checked={!!attrsOn[field]} onCheckedChange={(v) => setAttrsOn((p) => ({ ...p, [field]: !!v }))} />
                      <span className="font-semibold">{field}</span>
                      <span className="text-zinc-400 line-through">{ch.current || "—"}</span>
                      <span className="text-amber-600">→</span>
                      <span className="font-semibold text-zinc-900">{ch.new}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm border-zinc-300">{t("common.cancel")}</Button>
          <Button onClick={apply} disabled={busy || !preview || totalChanges === 0} data-testid="role-apply-confirm" className="rounded-sm bg-zinc-900 text-white hover:bg-zinc-800">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Layers size={16} className="mr-2" />}
            {t("roleApply.applyBtn")} {totalChanges > 0 ? `(${totalChanges})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
